import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, Edit, Trash2, Upload, MapPin, Home, DollarSign, Calendar,
    Bed, Bath, Square, Eye, Mail, Heart, Check, Image as ImageIcon
} from 'lucide-react';
import { NotificationManager } from 'react-notifications';

import DeleteConfirmationModal from '../../Components/LandlordProperty/DeleteConfirmationModal';
import landlordPropertyService from '../../services/landlordPropertyService';
import {
    formatCurrency,
    getPropertyTypeName,
    getAmenitiesByIds,
    getStatusBadgeClasses
} from '../../constants/propertyConstants';

/**
 * PropertyDetails Component
 * Displays detailed information about a specific property
 */
const PropertyDetails = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();

    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    useEffect(() => {
        loadProperty();
    }, [propertyId]);

    const loadProperty = async () => {
        setLoading(true);
        try {
            const data = await landlordPropertyService.getPropertyDetails(propertyId);
            setProperty(data);
        } catch (error) {
            console.error('Error loading property:', error);
            NotificationManager.error('Failed to load property details', 'Error');
            navigate('/landlord/properties');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        navigate(`/landlord/properties/edit/${propertyId}`);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await landlordPropertyService.deleteProperty(propertyId);
            NotificationManager.success('Property deleted successfully', 'Success');
            navigate('/landlord/properties');
        } catch (error) {
            console.error('Error deleting property:', error);
            NotificationManager.error('Failed to delete property', 'Error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            await landlordPropertyService.publishProperty(propertyId);
            NotificationManager.success('Property published successfully', 'Success');
            loadProperty();
        } catch (error) {
            console.error('Error publishing property:', error);
            NotificationManager.error('Failed to publish property', 'Error');
        } finally {
            setIsPublishing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading property details...</p>
                </div>
            </div>
        );
    }

    if (!property) {
        return null;
    }

    const selectedAmenities = getAmenitiesByIds(property.amenityIds || []);

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/landlord/properties')}
                        className="flex items-center text-soil-600 hover:text-soil-900 mb-4"
                    >
                        <ChevronLeft className="h-5 w-5 mr-1" />
                        Back to Properties
                    </button>

                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                                <h1 className="font-display text-3xl font-bold text-soil-900">
                                    {property.propertyName}
                                </h1>
                                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeClasses(property.status)}`}>
                                    {property.status?.toUpperCase()}
                                </span>
                            </div>
                            <div className="flex items-center text-soil-600">
                                <MapPin className="h-5 w-5 mr-2" />
                                <span>
                                    {property.addressLine1}
                                    {property.addressLine2 && `, ${property.addressLine2}`}
                                    , {property.city}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleEdit}
                                className="flex items-center px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600 transition-colors"
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </button>
                            {property.status === 'draft' && (
                                <button
                                    onClick={handlePublish}
                                    disabled={isPublishing}
                                    className="flex items-center px-4 py-2 bg-dawn-500 text-soil-900 rounded-lg hover:bg-dawn-600 disabled:opacity-50 transition-colors"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {isPublishing ? 'Publishing...' : 'Publish'}
                                </button>
                            )}
                            <button
                                onClick={() => setDeleteModal(true)}
                                className="p-2 bg-murram-500 text-white rounded-lg hover:bg-murram-600 transition-colors"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Images */}
                {property.images && property.images.length > 0 ? (
                    <div className="mb-8">
                        <div className="grid grid-cols-4 gap-2">
                            <div className="col-span-4 md:col-span-2 md:row-span-2">
                                <img
                                    src={property.images[0]}
                                    alt="Featured"
                                    className="w-full h-96 object-cover rounded-lg"
                                />
                            </div>
                            {property.images.slice(1, 5).map((image, index) => (
                                <img
                                    key={index}
                                    src={image}
                                    alt={`Property ${index + 2}`}
                                    className="w-full h-48 object-cover rounded-lg"
                                />
                            ))}
                        </div>
                        {property.totalImages > 5 && (
                            <p className="text-sm text-soil-600 mt-2 text-center">
                                +{property.totalImages - 5} more photos
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="mb-8 bg-soil-100 rounded-lg p-8 text-center">
                        <ImageIcon className="mx-auto h-12 w-12 text-soil-400 mb-3" />
                        <p className="text-soil-600">No images uploaded yet</p>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Total Views</p>
                                <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{property.viewCount || 0}</p>
                            </div>
                            <div className="p-3 bg-jacaranda-100 rounded-lg">
                                <Eye className="h-6 w-6 text-jacaranda-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Inquiries</p>
                                <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{property.inquiryCount || 0}</p>
                            </div>
                            <div className="p-3 bg-highland-50 rounded-lg">
                                <Mail className="h-6 w-6 text-highland-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Favorites</p>
                                <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{property.favoriteCount || 0}</p>
                            </div>
                            <div className="p-3 bg-murram-100 rounded-lg">
                                <Heart className="h-6 w-6 text-murram-500" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Description */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Description</h2>
                            <p className="text-soil-600 whitespace-pre-line">
                                {property.propertyDescription || 'No description provided.'}
                            </p>
                        </div>

                        {/* Property Details */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Property Details</h2>
                            <dl className="grid grid-cols-2 gap-4">
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Property Type</dt>
                                    <dd className="text-sm text-soil-900">{getPropertyTypeName(property.propertyTypeId)}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Bedrooms</dt>
                                    <dd className="text-sm text-soil-900 flex items-center">
                                        <Bed className="h-4 w-4 mr-1" />
                                        {property.bedrooms || 0}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Bathrooms</dt>
                                    <dd className="text-sm text-soil-900 flex items-center">
                                        <Bath className="h-4 w-4 mr-1" />
                                        {property.bathrooms || 0}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Square Feet</dt>
                                    <dd className="text-sm text-soil-900 flex items-center">
                                        <Square className="h-4 w-4 mr-1" />
                                        {property.squareFeet || 'N/A'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Year Built</dt>
                                    <dd className="text-sm text-soil-900">{property.yearBuilt || 'N/A'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400 mb-1">Furnishing</dt>
                                    <dd className="text-sm text-soil-900 capitalize">{property.furnishingStatus || 'N/A'}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* Features & Amenities */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Features & Amenities</h2>
                            <div className="flex flex-wrap gap-2">
                                {property.hasParking && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        Parking ({property.parkingSpaces} spaces)
                                    </span>
                                )}
                                {property.hasElevator && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        Elevator
                                    </span>
                                )}
                                {property.hasSecurity && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        24/7 Security
                                    </span>
                                )}
                                {property.hasWifi && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        WiFi
                                    </span>
                                )}
                                {property.hasGenerator && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        Generator
                                    </span>
                                )}
                                {property.hasWaterBackup && (
                                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm font-medium rounded-full">
                                        Water Backup
                                    </span>
                                )}
                                {selectedAmenities.map(amenity => (
                                    <span key={amenity.id} className="px-3 py-1 bg-soil-100 text-soil-900 text-sm font-medium rounded-full">
                                        {amenity.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Pricing */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Pricing</h2>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm text-soil-600">Monthly Rent</p>
                                    <p className="font-mono text-2xl font-bold text-soil-900">
                                        {formatCurrency(property.pricePerMonth, property.currency)}
                                    </p>
                                </div>
                                {property.securityDeposit && (
                                    <div>
                                        <p className="text-sm text-soil-600">Security Deposit</p>
                                        <p className="text-lg font-semibold text-soil-900">
                                            {formatCurrency(property.securityDeposit, property.currency)}
                                        </p>
                                    </div>
                                )}
                                {property.serviceCharge && (
                                    <div>
                                        <p className="text-sm text-soil-600">Service Charge</p>
                                        <p className="text-lg font-semibold text-soil-900">
                                            {formatCurrency(property.serviceCharge, property.currency)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Lease Terms */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Lease Terms</h2>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-sm font-medium text-soil-400">Lease Type</dt>
                                    <dd className="text-sm text-soil-900 capitalize mt-1">{property.leaseType}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400">Minimum Term</dt>
                                    <dd className="text-sm text-soil-900 mt-1">{property.minimumLeaseTerm} months</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400">Pet Friendly</dt>
                                    <dd className="text-sm text-soil-900 mt-1">{property.petFriendly ? 'Yes' : 'No'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-soil-400">Smoking</dt>
                                    <dd className="text-sm text-soil-900 mt-1">{property.smokingAllowed ? 'Allowed' : 'Not Allowed'}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal}
                onClose={() => setDeleteModal(false)}
                onConfirm={handleDelete}
                propertyName={property.propertyName}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default PropertyDetails;
